<script lang="ts" setup>
import { useBreakpoints, useScroll, useSwipe } from '@vueuse/core';
import type { Tables } from '~/types/database.types';
import {
  computed,
  defineOgImageComponent,
  onMounted,
  onUnmounted,
  ref,
  useAsyncData,
  useAuth,
  useDialog,
  useHead,
  useI18n,
  useMetrics,
  useRoute,
  useStorage,
  useSupabaseClient,
  useToast,
  watch,
} from '#imports';

const route = useRoute();
const supabase = useSupabaseClient();
const { showError } = useToast();
const { t } = useI18n();
const { currentUser } = useAuth();
const { openDialog } = useDialog();

const breakpoints = useBreakpoints({
  mobile: 920,
});

const isMobile = breakpoints.smaller('mobile');

type SpotWithRelations = Tables<'spots'> & {
  category_id: Tables<'categories'> | null;
  cities_id: Tables<'cities'> | null;
  map_id: {
    title: string;
    id: string;
    user_id: {
      mappr_handle: string;
      id: string;
    };
  } | null;
};

const emit = defineEmits([
  'close',
  'pauseTimer',
  'resumeTimer',
  'resetTimer',
  'next',
  'prev',
  'subscribe',
]);
const props = defineProps<{
  mapId: string;
  creator: Tables<'profiles'>;
}>();

const isSubscriber = computed(() => currentUser.value?.hasActiveSubscription);
const supportsThisCreator = computed(() =>
  currentUser.value?.supports?.some(
    (support) => support.creator_id === props.creator.id,
  ),
);
const isCreator = computed(
  () => currentUser.value?.profile?.id === props.creator,
);

let spotId = route.params.spotId as string;

watch(route, async (newRoute) => {
  console.log('the params changed');
  spotId = newRoute.params.spotId as string;
  await refresh();
});
const {
  data: spot,
  pending: spotPending,
  refresh,
} = await useAsyncData<SpotWithRelations | null>('spot:{id}', async () => {
  const { data, error } = await supabase
    .rpc('get_spot_by_slug', {
      search_slug_param: spotId,
    })
    .select(`
      *,
      category_id:categories (
        *
      ),
      cities_id:cities (
        *
      ),
      map_id:maps (
        title,
        id,
        current_slug,
        user_id:profiles (
          mappr_handle,
          id
        )
      )
    `)
    .single();

  if (error) showError(t('messages.maps.fetchSpots'));
  return data as SpotWithRelations;
});

// useHead(() => ({
//   name: spot.value.name,
//   meta: [
//     {
//       name: 'description',
//       content: spot.value.summary,
//     },
//   ],
// }));

// const { formatedImageUrl } = useStorage();
// defineOgImageComponent('Default', {
//   name: spot.value.name,
//   description:
//     spot.value.summary.split(' ').slice(0, 10).join(' ') +
//     (spot.value.summary.length > 90 ? '...' : ''),
//   imagePath: formatedImageUrl(spot.value.cover_image_url, 1200, 600, 75),
// });

// Navigate prev/next with left/right arrow keys
const handleKeyup = (event: KeyboardEvent) => {
  if (event.key === 'ArrowLeft') {
    emit('prev');
  } else if (event.key === 'ArrowRight') {
    emit('next');
  }
};

onMounted(() => {
  window.addEventListener('keyup', handleKeyup);
});

onUnmounted(() => {
  window.removeEventListener('keyup', handleKeyup);
});

const handleClose = () => {
  emit('close');
};

const googleMapsUrl = computed(() => {
  if (spot.value?.google_place_id) {
    return `https://www.google.com/maps/search/?api=1&query=${spot.value.lat},${spot.value.lon}&query_place_id=${spot.value.google_place_id}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${spot.value.lat},${spot.value.lon}`;
});

const bookSpotUrl = computed(() => {
  // probably implement the event tracking here?
  if (spot.value.booking_link) {
    return spot.value.booking_link;
  }
  if (spot.value.website) {
    return spot.value.website;
  }
  return googleMapsUrl.value;
});

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const el = ref<HTMLElement | null>(null);
const { y } = useScroll(el);

watch(y, () => {
  if (y.value > 0) {
    emit('pauseTimer');
  } else if (y.value === 0) {
    emit('resumeTimer');
  }
});

const { trackEvent } = useMetrics();
const handleBookingIntent = (url: string) => {
  if (spot.value) {
    trackEvent('booking_intent', {
      map_id: props.mapId,
      creator_id: props.creator.id,
      spot_id: spot.value.id || '',
      spot_name: spot.value.name || '',
      spot_destination: spot.value.cities_id?.name || '',
      spot_category: spot.value.category_id?.name || '',
    });
    openInNewTab(url);
  }
};

const handleOpenMaps = (url: string) => {
  if (spot.value) {
    trackEvent('open_spot_in_google_maps', {
      map_id: props.mapId,
      creator_id: props.creator.id,
      spot_id: spot.value.id || '',
      spot_name: spot.value.name || '',
      spot_destination: spot.value.cities_id?.name || '',
      spot_category: spot.value.category_id?.name || '',
    });
  }
  openInNewTab(url);
};

const spotHeader = ref(null);
const { isSwiping, direction } = useSwipe(spotHeader);

watch(isSwiping, () => {
  if (direction.value === 'right') {
    emit('prev');
  } else if (direction.value === 'left') {
    emit('next');
  }
});

const handleCoverTouch = (e) => {
  if (e.changedTouches[0].clientY > (window.innerHeight / 6) * 5) return;
  if (e.changedTouches[0].clientX > (window.innerWidth / 6) * 5) {
    emit('next');
  } else if (e.changedTouches[0].clientX < window.innerWidth / 6) {
    emit('prev');
  }
};

onMounted(() => {
  if (spot.value) {
    trackEvent('viewed_spot', {
      creator_id: spot.value.map_id?.user_id?.id,
      creator_handle: spot.value.map_id?.user_id?.mappr_handle,
      spot_id: spot.value.id,
      spot_name: spot.value.name,
      spot_destination: spot.value.cities_id?.name,
      spot_category: spot.value.category_id?.name,
      map_name: spot.value.map_id?.name,
      map_id: spot.value.map_id?.id,
    });
  }
});

const showPreviewOverlay = computed(() => {
  if (!spot.value) return false;
  if (isCreator.value) return false;

  const visibilityChecks = {
    authenticated: !currentUser.value,
    subscriber: !isSubscriber.value,
    supporter: !supportsThisCreator.value,
  };

  return visibilityChecks[spot.value.visibility] || false;
});

const determineVisibilityType = () => {
  if (!currentUser.value) return 'public';
  if (currentUser.value && !isSubscriber.value && !supportsThisCreator.value)
    return 'authenticated';
  if (currentUser.value && isSubscriber.value && !supportsThisCreator.value)
    return 'subscribed';
  if (currentUser.value && supportsThisCreator.value) return 'supporter';
  return null;
};

const openShareDialog = () => {
  if (!spot.value) return;
  try {
    openDialog('MPShare', {
      map: spot.value,
      subtitle: 'Share spot:',
      version: 'spot',
    });
  } catch (e) {
    showError('Error opening share dialog');
  }
};
</script>
<template>
  <div ref="el" class="spot" @touchstart="emit('pauseTimer')" @touchend="y === 0 ? emit('resumeTimer') : null">
    <div v-if="spot && !spotPending" class="spot-container animate-zoom-pan fadeInOut">
      <div v-if="showPreviewOverlay" class="preview-overlay">
        <MPSpotTierOverlay :visibility="determineVisibilityType()" :creator="creator" @subscribe="emit('subscribe', $event)" @close="handleClose"/>
      </div>
      <div ref="spotHeader" class="spot-header">
        <div>
          <i class="ri-close-line close-icon" @click="handleClose" />
        </div>
        <div v-if="!showPreviewOverlay" class="spot-actions">
          <MPBookmark v-if="spot.id" link class="mobile-bookmark" bookmark="spot_bookmarks" :spot-id="spot.id"  />
          <MPCopyRightText
            class="desktop-copyright"
            v-if="spot.cover_image_copyright"
            :copyright-text="spot.cover_image_copyright"
          />
        </div>
      </div>
      <div class="spot-info background" :style="`background-image: url(${spot.cover_image_url})`">
        <div class="spot-info-wrappers" @touchend="handleCoverTouch">
          <div class="spot-info-mobile">
            <h1>{{ spot.name }}</h1>
            <div v-if="spot.category_id" size="large" class="category">
              <i v-if="spot.category_id.icon" :class="spot.category_id.icon" />
              {{ spot.category_id.name }}
            </div>
            <p>
              {{ spot.summary.substring(0, 300) }}{{ spot.summary.length > 300 ? '...' : '' }}
            </p>
            <MPCopyRightText
              class="mobile-bookmark"
              v-if="spot.cover_image_copyright"
              :copyright-text="spot.cover_image_copyright"
            />
          </div>

        </div>
      </div>
      <div class="spot-details">
        <div class="trigger">
          <i class="ri-arrow-up-wide-line" />
          Swipe up to discover more
        </div>
        <div class="spot-details-content">
          <h1>{{ spot.name }}</h1>
          <div v-if="spot.category_id" size="large" class="category">
            <i v-if="spot.category_id.icon" :class="spot.category_id.icon" />
            {{ spot.category_id.name }}
          </div>
          <div class="book-spot-buttons">
            <MPButton
              label="Book Now"
              version="primary"
              rounded
              class="book-spot-button"
              @click="handleBookingIntent(bookSpotUrl)"
            />
            <MPButton
              label="Open in Maps"
              rounded
              class="open-maps-button"
              @click="handleOpenMaps(googleMapsUrl)"
            />
            <MPBookmark
              v-if="spot.id"
              :spot-id="spot.id"
              background-color="var(--color-slate-100)"
              icon-color="var(--color-rose-500)"
              link
              class="desktop-bookmark"
              bookmark="spot_bookmarks"
            />
            <MPButton
              :hide-label="!isMobile"
              :label="isMobile ? 'Share Spot' : ''"
              rounded
              icon="ri-share-line"
              version="secondary"
              class="share-spot-button"
              @click="openShareDialog"
            />
          </div>
          <p>
            {{ spot.summary }}
          </p>
          <hr>
          <div>
            <p v-if="spot.known_for" class="spot-detail">
              <span>Known for:</span> {{ spot.known_for }}
            </p>
            <p v-if="spot.why_go" class="spot-detail">
              <span>Why go:</span> {{ spot.why_go }}
            </p>
            <p v-if="spot.vibe" class="spot-detail">
              <span>Vibe:</span> {{ spot.vibe }}
            </p>
            <p v-if="spot.transport" class="spot-detail">
              <span>Transport:</span> {{ spot.transport }}
            </p>
            <p v-if="spot.tips" class="spot-detail">
              <span>Tips:</span> {{ spot.tips }}
            </p>
          </div>

          <div class="book-spot-details">
            <p v-if="spot.website" class="spot-detail">
              <span>Website: </span>
              <a class="spot-website-link" @click="openInNewTab(spot.website)"> {{ spot.website }}</a>
            </p>
            <p v-if="spot.formatted_address" class="spot-detail">
              <span>Address:</span> {{ spot.formatted_address }}
            </p>
          </div>
          <ClientOnly>
            <MPMap gesture-handling="none" :locations="[spot]" class="map" :zoom="12" :fit-bounds="false" />
          </ClientOnly>
        </div>
      </div>
      </div>
    </div>
</template>

<style scoped lang="postcss">
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/typography.pcss";

.book-spot-details {
  display: grid;
  .spot-website-link {
    word-break: break-all;
  }
}

.book-spot-buttons {
  position: relative;
  display: grid;
  gap: 1rem;
  padding: 1 0;
  @mixin screen md {
    display: flex;
    position: absolute;
    left: 1rem;
    top: -2.5rem;
    padding: 1rem 0;

  }
}

.preview-overlay {
  background: rgba(0, 0, 0, 0.4);
  z-index: 200;
  border-radius: 1rem;
  max-height: 100%;
  padding: 1.5rem 1rem 1rem;
  @mixin screen md {
    padding: 1rem;
  }
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    background-size: auto 120%
  }
  2% {
    opacity: 1;
  }
  100% {
    background-size: auto 100%
  }
}

.fadeInOut {
  animation: fadeInOut 20s ease-in-out;
}
/*
@keyframes zoomPan {
  0% {
    background-size: auto 120%
  }
  100% {
    background-size: auto 100%
  }
}

.animate-zoom-pan {
  animation: zoomPan 20s ease-out forwards;
} */


.spot {
  z-index: 50;
  position: absolute;
  overflow-y: scroll;
  overflow-x: hidden;
  width: 100vw;
  height: 100vh;
  max-height:100%;
  text-align: center;
  color: var(--color-white);
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  .spot-container {
    position: relative;
  }

  .spot-header {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: fit-content;
    z-index: 99;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    margin-top: 0.5rem;
    color: var(--color-white);
    i {
      width: 1.625rem;
      height: 1.625rem;
      font-size: 1.625rem;
      background-color: transparent;
      z-index: 1000;
    }
  }
  .background {
    height: 100vh;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }
  .spot-info {
    z-index: 0;
    width: 100vw;
    height: 100vh;
    z-index: 10;
  }
  .spot-info-wrappers {
    padding: 4rem 1rem;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.80) 20%, rgba(0, 0, 0, 0.60) 30%, rgba(0, 0, 0, 0.20) 50%, rgba(0, 0, 0, 1) 100%);


  }
}
h1 {
  font-size: 2.5rem;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  margin: 0 0 1rem;
}
.category {
  display: flex;
  align-content: center;
  margin-bottom: 2rem;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  i {

    font-size: 1rem;
    padding: 0.375rem;
    background-color: var(--color-indigo-400);
    border-radius: 100%;
  }

  @mixin screen md {
    color: var(--color-rose-500);
    @mixin typeface-summary;
    margin-bottom: 0.5rem;

    i {
      font-size: 1.5rem;
      background-color: transparent;
    }
  }
}
p {
  @mixin typeface-regular;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  margin-top: 0.5rem;
}
.spot-details {
  z-index: 10;
  text-align: center;
  position: relative;
  .trigger {
    display: grid;
    position: absolute;
    top: -5rem;
    left: 0;
    align-items: end;
    grid-template-rows: 1rem 1rem;
    gap: 1rem;
    width: 100%;
    background-color: transparent;
    color: var(--color-white);
    @mixin typeface-regular;
    i {
      font-size: 1.5rem;
    }

    @mixin screen md {
      display: none;
    }
  }
  .spot-details-content {
    margin-top: 0;
    min-height: 100vh;
    padding: 2rem 2rem 1rem;
    background-color: var(--main-background-color);
    color: var(--color-black);
    text-align: left;
    h1, .category {
      text-align: center;
    }
  }
  p {
    text-align: left;
  }
}

.spot-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 1.75rem;
  gap: 0.75rem;
  @mixin screen md {
    font-size: 1.5rem;
    gap: 0.5rem;
  }
}

.spot-detail {
  line-height: 1.25;
  margin-bottom: 1rem;
  span {
    font-weight: 700;
  }
  a {
    @mixin typeface-regular;
    color: var(--color-black);
    text-decoration: underline;
  }
}

.preview-overlay + .spot-header + .spot-info {
  position: absolute;
  top: 0;
  z-index: -1;
}
.preview-overlay + .spot-header + .spot-info + .spot-details {
  display: none;
}

.map {
  width: 100%;
  height: 10rem;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-top: 1rem;
}

@mixin screen md {
  .preview-overlay + .spot-header + .spot-info {
    position: absolute;
    top: 0;
    z-index: -1;
  }
  .preview-overlay + .spot-header + .spot-info + .spot-details {
    display: none;
  }
  .spot {
    width: 100%;
    position: relative;
    max-height: 100%;
    height: fit-content;
    background-color: var(--main-background-color);
    padding: 0;
    border-radius: 0.75rem;
    border: 1px solid var(--color-gray-100);
    .spot-header {
      width: 100%;
      height: 100%;
      z-index: 10;
      display: block;
      position: relative;
    }
    .background {
      height: 100vh;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }
    .spot-info,
    .spot-header {
      display: block;
      position: relative;
      width: 100%;
      height: fit-content;
      z-index: 10;
    }
    .spot-header {
      position: absolute;
      z-index: 99;
      top: 0;
      width: 100%;
      display: flex;
    }
    .spot-info-wrappers {
      width: 100%;
      height: fit-content;
      text-align: left;
      padding: 12rem 1rem 6rem;
      background: transparent;
      text-align: center;
      h1 {
        margin-top: 2rem;
        @mixin typeface-h2;
      }
      p {
        display: none;
      }
    }
    .spot-details-content {
      padding-top: 1rem;
      display: grid;
      align-items: start;
      .map-container {
        display: none;
      }
    }
    .background {
      width: 100%;
      height: fit-content;
      position: relative;
    }
    .spot-details-content {
      min-height: fit-content;
    }
    .spot-details-content h1 {
      @mixin screen md {
        margin-top: 2rem;
        @mixin typeface-h2;
        text-align: left;
      }
    }
    .spot-details-content .category {
      @mixin screen md {
        text-align: left;
        justify-content: flex-start;
      }
    }
  }
}

.map {
  @mixin screen md {
    display: none;
  }
}

.mobile-bookmark {
  display: block;

  @mixin screen md {
    display: none
  }
}

.desktop-bookmark {
  display: none;

  @mixin screen md {
    display: block;
  }
}

.mobile-copyright {
  display: block;

  @mixin screen md {
    display: none
  }
}

.desktop-copyright {
  display: none;

  @mixin screen md {
    display: block;
  }
}

.spot-info-mobile {
  display: block;

  @mixin screen md {
    display: none;
  }
}

</style>
